/**
 * NavGroup Component
 *
 * Collapsible navigation group for organizing related menu items.
 * Reduces cognitive overload by grouping 19+ tabs into logical categories.
 * Supports keyboard navigation (Arrow keys, Home, End, Escape).
 *
 * @example
 * <NavGroup title="Analysis" icon={Brain} defaultOpen>
 *   <NavItem id="skills" label="Skills Matrix" icon={Target} />
 *   <NavItem id="learning" label="Learning Paths" icon={GraduationCap} />
 * </NavGroup>
 */

import React, { useState, useCallback, useRef, useEffect, Children, cloneElement } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Collapsible navigation group with keyboard support
 */
export const NavGroup = ({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
  className = '',
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  // Get valid children count
  const childArray = Children.toArray(children).filter(
    child => child && child.type && child.type !== NavDivider
  );
  const itemCount = childArray.length;

  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  /**
   * Focus a specific item by index
   */
  const focusItem = useCallback((index) => {
    if (index >= 0 && index < itemCount) {
      setFocusedIndex(index);
      itemRefs.current[index]?.focus();
    }
  }, [itemCount]);

  /**
   * Handle keyboard navigation within the group
   */
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItem(focusedIndex < itemCount - 1 ? focusedIndex + 1 : 0);
        break;

      case 'ArrowUp':
        e.preventDefault();
        focusItem(focusedIndex > 0 ? focusedIndex - 1 : itemCount - 1);
        break;

      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;

      case 'End':
        e.preventDefault();
        focusItem(itemCount - 1);
        break;

      case 'Escape':
        e.preventDefault();
        close();
        break;

      default:
        break;
    }
  }, [isOpen, focusedIndex, itemCount, focusItem, close]);

  /**
   * Handle header keyboard events (toggle on Enter/Space)
   */
  const handleHeaderKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'ArrowDown' && isOpen) {
      e.preventDefault();
      focusItem(0);
    }
  }, [toggle, isOpen, focusItem]);

  // Reset focused index when closing
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  // Clone children with refs and keyboard props
  const enhancedChildren = Children.map(children, (child, index) => {
    if (!child || !child.type || child.type === NavDivider) {
      return child;
    }

    // Find the actual index among non-divider children
    const actualIndex = childArray.indexOf(child);

    return cloneElement(child, {
      ref: (el) => (itemRefs.current[actualIndex] = el),
      tabIndex: focusedIndex === actualIndex ? 0 : -1,
      onKeyDown: handleKeyDown,
      'data-nav-index': actualIndex,
    });
  });

  return (
    <div className={`mb-1 ${className}`} role="navigation">
      <button
        onClick={toggle}
        onKeyDown={handleHeaderKeyDown}
        className="w-full flex items-center justify-between px-3 py-2 text-left text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50"
        aria-expanded={isOpen}
        aria-controls={`nav-group-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center space-x-2">
          {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
          <span className="text-xs font-medium uppercase tracking-wider">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
      </button>

      <div
        ref={containerRef}
        id={`nav-group-${title.toLowerCase().replace(/\s+/g, '-')}`}
        role="menu"
        aria-label={`${title} navigation items`}
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-2 mt-1 space-y-1">
          {enhancedChildren}
        </div>
      </div>
    </div>
  );
};

/**
 * Individual navigation item with keyboard support
 */
export const NavItem = React.forwardRef(({
  id,
  label,
  icon: Icon,
  isActive,
  onClick,
  badge,
  disabled = false,
  className = '',
  tabIndex = 0,
  onKeyDown,
  ...props
}, ref) => {
  /**
   * Handle item activation via keyboard
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) {
        onClick?.(id);
      }
    }
    // Pass through to parent handler for navigation
    onKeyDown?.(e);
  }, [id, disabled, onClick, onKeyDown]);

  return (
    <button
      ref={ref}
      onClick={() => !disabled && onClick?.(id)}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      tabIndex={tabIndex}
      role="menuitem"
      className={`
        w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors
        focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-inset
        ${isActive
          ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
          : disabled
            ? 'text-gray-600 cursor-not-allowed'
            : 'text-gray-300 hover:bg-white/5 hover:text-white'
        }
        ${className}
      `}
      style={{ minHeight: '40px' }}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={disabled}
      {...props}
    >
      <div className="flex items-center space-x-3">
        {Icon && <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
        <span className="text-sm">{label}</span>
      </div>
      {badge && (
        <span className="text-xs bg-primary-500/20 text-primary-300 px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
});

NavItem.displayName = 'NavItem';

/**
 * Navigation divider
 */
export const NavDivider = ({ className = '' }) => (
  <div
    className={`my-2 border-t border-white/10 ${className}`}
    role="separator"
    aria-hidden="true"
  />
);

/**
 * Standalone nav item (not in a group) with keyboard support
 */
export const NavItemStandalone = React.forwardRef(({
  id,
  label,
  icon: Icon,
  isActive,
  onClick,
  badge,
  className = '',
  tabIndex = 0,
  ...props
}, ref) => {
  /**
   * Handle item activation via keyboard
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(id);
    }
  }, [id, onClick]);

  return (
    <button
      ref={ref}
      onClick={() => onClick?.(id)}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      role="menuitem"
      className={`
        w-full flex items-center justify-between px-3 py-3 rounded-lg text-left transition-colors mb-1
        focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-inset
        ${isActive
          ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
          : 'text-gray-300 hover:bg-white/5 hover:text-white'
        }
        ${className}
      `}
      style={{ minHeight: '44px' }}
      aria-current={isActive ? 'page' : undefined}
      {...props}
    >
      <div className="flex items-center space-x-3">
        {Icon && <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />}
        <span className="text-sm font-medium">{label}</span>
      </div>
      {badge && (
        <span className="text-xs bg-primary-500/20 text-primary-300 px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
});

NavItemStandalone.displayName = 'NavItemStandalone';

export default NavGroup;
